// Reshapes the flat getSinClassification rows into the collections the SIN
// selection screens used to get from three separate calls.

export const FED_STANDARD_UNIQUE_KEY = 'fedStandardCodes';
export const VEHICLE_TYPE_UNIQUE_KEY = 'vehicleType';

// programStatus is a smallint typed as String in GraphQL, so it arrives as 1
// or "1"; 'Active' is how the other standards code endpoints spell it.
export const isActiveStatus = (status) => {
  if (status === null || status === undefined) return false;
  const value = `${status}`.toLowerCase();
  return value === '1' || value === 'active';
};

const labelWithCode = (code, title) =>
  title ? `${code} - ${title}` : `${code}`;

// ids stay strings: mergeSelections() compares them with includes()
export const toStandardItems = (rows = []) =>
  rows.map((row) => ({
    id: String(row.id),
    standardItemNumber: row.standardItemNumber,
    title: row.standardItemTitle,
    fedStandard: {
      code: row.fedStandardCode,
      title: row.fedStandardCodeTitle,
    },
    vehicleTypeCode: {
      code: row.vehicleType,
      title: row.vehicleTypeTitle,
    },
  }));

// carries both uniqueKeys that getVehicleClassificationFVS used to return: the
// screens read the fedStandardCodes entries, InactiveSIToaster the vehicleType
// ones
export const toVehicleClassifications = (rows = []) => {
  const fedStandardCodes = new Map();
  const vehicleTypes = new Map();
  rows.forEach((row) => {
    if (row.fedStandardCode && !fedStandardCodes.has(row.fedStandardCode)) {
      fedStandardCodes.set(row.fedStandardCode, {
        id: `${row.fedStandardCode}${FED_STANDARD_UNIQUE_KEY}`,
        label: labelWithCode(row.fedStandardCode, row.fedStandardCodeTitle),
        value: row.fedStandardCode,
        type: FED_STANDARD_UNIQUE_KEY,
        uniqueKey: FED_STANDARD_UNIQUE_KEY,
        currentYear: row.year,
        parent: row.program,
      });
    }
    if (row.vehicleType && !vehicleTypes.has(row.vehicleType)) {
      vehicleTypes.set(row.vehicleType, {
        id: `${row.vehicleType}${VEHICLE_TYPE_UNIQUE_KEY}`,
        label: labelWithCode(row.vehicleType, row.vehicleTypeTitle),
        value: row.vehicleType,
        type: VEHICLE_TYPE_UNIQUE_KEY,
        uniqueKey: VEHICLE_TYPE_UNIQUE_KEY,
        currentYear: row.year,
        parent: row.program,
      });
    }
  });
  return [...fedStandardCodes.values(), ...vehicleTypes.values()];
};

// Inverted on purpose: `label` is the code, `value` is the "code - title"
// display string that the save handlers split back on '-'.
export const toProgramDropdownOptions = (rows = []) => {
  const byCode = new Map();
  rows.forEach((row) => {
    if (!row.program || byCode.has(row.program)) return;
    if (!isActiveStatus(row.programStatus)) return;
    byCode.set(row.program, {
      label: row.program,
      value: labelWithCode(row.program, row.programTitle),
    });
  });
  return [...byCode.values()];
};

// getSinClassification only returns programs that still have an active SIN, so
// a saved program without one would drop out and be removed on the next save.
export const withSavedPrograms = (programOptions = [], programs = []) => {
  const merged = [...programOptions];
  programs.forEach(({ program }) => {
    if (!program || merged.some((option) => option.label === program)) return;
    merged.push({ label: program, value: `${program}` });
  });
  return merged;
};
